import { useCallback, useRef, useState, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { UploadCloud } from '@digital-twins/design-system/icons'
import { cn } from '@/lib/utils'

interface FileUploadZoneProps {
  /** Visual style of the upload zone */
  variant?: 'dataset' | 'context'
  accept?: string
  multiple?: boolean
  disabled?: boolean
  onFilesSelected: (files: FileList) => void
  className?: string
  /** Overrides the hardcoded primary copy line when provided (e.g. composer-facing callers). */
  title?: string
  /** Overrides the hardcoded secondary copy line when provided (e.g. composer-facing callers). */
  subtitle?: string
  /** Drops the large decorative SVG illustration (context variant only draws one). */
  hideIllustration?: boolean
  /** Context variant only — a compact glyph rendered in place of the decorative illustration
   *  (e.g. an ImagePlus icon for an upload-area look). Takes precedence over the illustration. */
  icon?: ReactNode
}

function ContextUploadIllustration() {
  return (
    <svg
      width="143"
      height="71"
      viewBox="0 0 143 71"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="mb-2 shrink-0"
    >
      <path d="M57.6211 0.520508C66.0004 0.203047 73.709 3.59341 79.3887 9.33984L79.7021 9.65625L80.0527 9.38281C84.0318 6.27623 88.8748 4.37059 94.1445 4.1709C106.353 3.70835 115.148 11.214 117.89 19.0342L118.123 19.7002L118.674 19.2588C118.837 19.1285 119.223 18.926 119.794 18.7275C120.351 18.534 121.043 18.3586 121.786 18.2617C132.258 16.8965 142.116 25.739 142.488 38.1562V38.1572C142.675 43.8989 141.177 49.3811 137.856 53.3447C134.549 57.293 129.387 59.7897 122.117 59.5H122.098L23.2793 59.292C16.4881 58.9977 10.9038 56.6184 6.96875 52.7881C3.03095 48.955 0.715112 43.6403 0.512695 37.4248C0.107544 24.981 9.3002 14.4326 19.79 14.0352C24.0736 13.873 28.0789 15.2487 31.3506 17.709L31.8271 18.0684L32.0967 17.5361C37.0658 7.727 46.5535 0.939827 57.6211 0.520508Z" fill="url(#paint0_linear_886_5692)" stroke="#3E6161" style={{ stroke: 'var(--illustration-stroke)' }} />
      <mask id="mask0_886_5692" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="57" y="35" width="29" height="35">
        <path d="M85.1013 51.769C85.6375 52.4216 85.1732 53.4038 84.3286 53.4038H79.4406C78.8883 53.4038 78.4406 53.8516 78.4406 54.4038V68.8413C78.4406 69.3936 77.9928 69.8413 77.4406 69.8413H65.3517C64.7994 69.8413 64.3517 69.3936 64.3517 68.8413V54.4038C64.3517 53.8516 63.904 53.4038 63.3517 53.4038H58.4637C57.6191 53.4038 57.1549 52.4216 57.691 51.769L70.6239 36.0288C71.024 35.5419 71.7692 35.5419 72.1693 36.0288L85.1013 51.769Z" fill="#D9D9D9" />
      </mask>
      <g mask="url(#mask0_886_5692)">
        <path d="M85.352 52.0736C85.7882 52.6044 85.4105 53.4034 84.7235 53.4034H79.2541C78.8048 53.4034 78.4406 53.7676 78.4406 54.2168V69.0277C78.4406 69.477 78.0765 69.8411 77.6272 69.8411H65.1653C64.716 69.8411 64.3518 69.477 64.3518 69.0277V54.2168C64.3518 53.7676 63.9877 53.4034 63.5384 53.4034H58.0689C57.3819 53.4034 57.0043 52.6044 57.4404 52.0736L70.7677 35.8528C71.0931 35.4567 71.6993 35.4567 72.0247 35.8528L85.352 52.0736Z" fill="#D9D9D9" />
        <rect x="54.2282" y="33.0487" width="39.9683" height="38.569" rx="5.14857" transform="rotate(5.3955 54.2282 33.0487)" fill="#0A2120" />
        <g filter="url(#filter0_fn_886_5692)">
          <ellipse cx="67.3248" cy="65.1188" rx="12.0255" ry="13.1187" transform="rotate(5.3955 67.3248 65.1188)" fill="#96FDD8" />
        </g>
        <g filter="url(#filter1_fn_886_5692)">
          <path d="M78.8924 40.9092C78.3201 46.9682 67.0519 57.7117 58.7719 56.9297C50.492 56.1476 44.8851 43.8104 45.4573 37.7513C46.0296 31.6923 53.2058 27.4144 61.4858 28.1964C69.7657 28.9785 79.4647 34.8501 78.8924 40.9092Z" fill="#134B4E" />
        </g>
        <g filter="url(#filter2_fn_886_5692)">
          <path d="M64.6551 56.8164C64.2435 61.1746 59.8829 64.3272 54.9155 63.858C49.9481 63.3889 46.2549 59.4756 46.6665 55.1174C47.0781 50.7592 47.3105 32.3933 52.2779 32.8624C57.2453 33.3316 65.0667 52.4582 64.6551 56.8164Z" fill="#93DDAC" />
        </g>
        <g filter="url(#filter3_fn_886_5692)">
          <circle cx="79.3652" cy="50.225" r="14.9116" transform="rotate(5.3955 79.3652 50.225)" fill="#225D5C" />
        </g>
        <g filter="url(#filter4_fn_886_5692)">
          <ellipse cx="68.5883" cy="73.9327" rx="9.88275" ry="10.5824" transform="rotate(5.3955 68.5883 73.9327)" fill="#146558" />
        </g>
        <g filter="url(#filter5_fn_886_5692)">
          <path d="M86.448 10.3211C92.1821 8.69656 98.8038 14.3447 101.238 22.9366C103.672 31.5284 100.997 39.8105 95.2629 41.435C89.5288 43.0595 66.2792 52.4285 63.845 43.8366C61.4108 35.2448 80.7139 11.9456 86.448 10.3211Z" fill="#B9DD93" />
        </g>
        <g filter="url(#filter6_fn_886_5692)">
          <path d="M79.1339 65.7223C81.8529 64.952 85.0329 67.772 86.2367 72.0209C87.4405 76.2698 86.2121 80.3387 83.4931 81.109C80.7741 81.8793 69.7644 86.3743 68.5607 82.1254C67.3569 77.8765 76.4149 66.4927 79.1339 65.7223Z" fill="#93DDA6" />
        </g>
        <g filter="url(#filter7_f_886_5692)">
          <path d="M49.2454 57.0407C51.5033 56.401 54.6486 60.5238 56.2707 66.2492C57.8927 71.9746 57.3773 77.1345 55.1194 77.7742C52.8615 78.4139 43.9054 82.8051 42.2833 77.0797C40.6613 71.3544 46.9875 57.6804 49.2454 57.0407Z" fill="#D6FFCD" />
        </g>
        <g filter="url(#filter8_fn_886_5692)">
          <ellipse cx="89.4304" cy="35.4046" rx="9.88275" ry="10.5824" transform="rotate(5.3955 89.4304 35.4046)" fill="#146558" />
        </g>
        <g filter="url(#filter9_fn_886_5692)">
          <path d="M73.0066 54.9352C72.5842 59.4074 68.8503 62.7125 64.6666 62.3173C60.483 61.9222 64.6173 58.6549 65.0397 54.1828C65.4621 49.7106 62.0126 45.7271 66.1963 46.1222C70.3799 46.5174 73.429 50.4631 73.0066 54.9352Z" fill="#1A897A" />
        </g>
      </g>
      <path d="M85.3536 52.292C85.7854 52.8184 85.4115 53.6107 84.7314 53.6107H79.317C78.8723 53.6107 78.5117 53.9719 78.5117 54.4174V69.1049C78.5117 69.5504 78.1512 69.9116 77.7065 69.9116H65.3699C64.9252 69.9116 64.5647 69.5504 64.5647 69.1049V54.4174C64.5647 53.9719 64.2042 53.6107 63.7594 53.6107H58.345C57.6649 53.6107 57.2911 52.8184 57.7228 52.292L70.916 36.2062C71.2382 35.8134 71.8383 35.8134 72.1604 36.2062L85.3536 52.292Z" stroke="#3E6161" style={{ stroke: 'var(--illustration-stroke)' }} />
      <path d="M71.4847 39.1114L71.8789 39.4189L71.4847 39.1114ZM71.8001 39.1114L71.4058 39.4189L71.8001 39.1114ZM62.1443 51.0862L62.5386 51.3938L62.1443 51.0862ZM81.1405 51.0862L80.7462 51.3938L81.1405 51.0862ZM80.9827 51.4092V50.9092H76.5963V51.4092V51.9092H80.9827V51.4092ZM76.3963 51.6092H75.8963V67.3001H76.3963H76.8963V51.6092H76.3963ZM76.1963 67.5001V67.0001H66.8424V67.5001V68.0001H76.1963V67.5001ZM66.6424 67.3001H67.1424V51.6092H66.6424H66.1424V67.3001H66.6424ZM66.4424 51.4092V50.9092H62.302V51.4092V51.9092H66.4424V51.4092ZM62.1443 51.0862L62.5386 51.3938L71.8789 39.4189L71.4847 39.1114L71.0904 38.8039L61.7501 50.7787L62.1443 51.0862ZM71.8001 39.1114L71.4058 39.4189L80.7462 51.3938L81.1405 51.0862L81.5347 50.7787L72.1943 38.8039L71.8001 39.1114ZM71.4847 39.1114L71.8789 39.4189C71.7588 39.5729 71.526 39.5729 71.4058 39.4189L71.8001 39.1114L72.1943 38.8039C71.9141 38.4446 71.3707 38.4446 71.0904 38.8039L71.4847 39.1114ZM62.302 51.4092V50.9092C62.5519 50.9092 62.6922 51.1968 62.5386 51.3938L62.1443 51.0862L61.7501 50.7787C61.3916 51.2384 61.7191 51.9092 62.302 51.9092V51.4092ZM66.6424 51.6092H67.1424C67.1424 51.2226 66.829 50.9092 66.4424 50.9092V51.4092V51.9092C66.2767 51.9092 66.1424 51.7749 66.1424 51.6092H66.6424ZM66.8424 67.5001V67.0001C67.0081 67.0001 67.1424 67.1344 67.1424 67.3001H66.6424H66.1424C66.1424 67.6867 66.4558 68.0001 66.8424 68.0001V67.5001ZM76.3963 67.3001H75.8963C75.8963 67.1344 76.0306 67.0001 76.1963 67.0001V67.5001V68.0001C76.5829 68.0001 76.8963 67.6867 76.8963 67.3001H76.3963ZM76.5963 51.4092V50.9092C76.2097 50.9092 75.8963 51.2226 75.8963 51.6092H76.3963H76.8963C76.8963 51.7749 76.762 51.9092 76.5963 51.9092V51.4092ZM80.9827 51.4092V51.9092C81.5657 51.9092 81.8932 51.2384 81.5347 50.7787L81.1405 51.0862L80.7462 51.3938C80.5925 51.1968 80.7329 50.9092 80.9827 50.9092V51.4092Z" fill="#3E6161" style={{ fill: 'var(--illustration-stroke)' }} />
      <defs>
        <filter id="filter0_fn_886_5692" x="47.3264" y="44.0466" width="39.9968" height="42.1442" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="3.98095" result="effect1_foregroundBlur_886_5692" />
          <feTurbulence type="fractalNoise" baseFrequency="39.375003814697266 39.375003814697266" stitchTiles="stitch" numOctaves={3} result="noise" seed={3062} />
          <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
          <feComponentTransfer in="alphaNoise" result="coloredNoise1">
            <feFuncA type="discrete" tableValues="0 0 0 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0" />
          </feComponentTransfer>
          <feComposite operator="in" in2="effect1_foregroundBlur_886_5692" in="coloredNoise1" result="noise1Clipped" />
          <feFlood floodColor="rgba(0, 43, 34, 0.25)" result="color1Flood" />
          <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
          <feMerge result="effect2_noise_886_5692">
            <feMergeNode in="effect1_foregroundBlur_886_5692" />
            <feMergeNode in="color1" />
          </feMerge>
        </filter>
        <filter id="filter1_fn_886_5692" x="37.4553" y="20.1423" width="49.4231" height="44.7897" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="3.98095" result="effect1_foregroundBlur_886_5692" />
          <feTurbulence type="fractalNoise" baseFrequency="39.375003814697266 39.375003814697266" stitchTiles="stitch" numOctaves={3} result="noise" seed={3062} />
          <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
          <feComponentTransfer in="alphaNoise" result="coloredNoise1">
            <feFuncA type="discrete" tableValues="0 0 0 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0" />
          </feComponentTransfer>
          <feComposite operator="in" in2="effect1_foregroundBlur_886_5692" in="coloredNoise1" result="noise1Clipped" />
          <feFlood floodColor="rgba(0, 43, 34, 0.25)" result="color1Flood" />
          <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
          <feMerge result="effect2_noise_886_5692">
            <feMergeNode in="effect1_foregroundBlur_886_5692" />
            <feMergeNode in="color1" />
          </feMerge>
        </filter>
        <filter id="filter2_fn_886_5692" x="38.6733" y="24.8917" width="33.9593" height="46.9745" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="3.98095" result="effect1_foregroundBlur_886_5692" />
          <feTurbulence type="fractalNoise" baseFrequency="39.375003814697266 39.375003814697266" stitchTiles="stitch" numOctaves={3} result="noise" seed={3062} />
          <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
          <feComponentTransfer in="alphaNoise" result="coloredNoise1">
            <feFuncA type="discrete" tableValues="0 0 0 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0" />
          </feComponentTransfer>
          <feComposite operator="in" in2="effect1_foregroundBlur_886_5692" in="coloredNoise1" result="noise1Clipped" />
          <feFlood floodColor="rgba(0, 43, 34, 0.25)" result="color1Flood" />
          <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
          <feMerge result="effect2_noise_886_5692">
            <feMergeNode in="effect1_foregroundBlur_886_5692" />
            <feMergeNode in="color1" />
          </feMerge>
        </filter>
        <filter id="filter3_fn_886_5692" x="56.4907" y="27.3505" width="45.749" height="45.749" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="3.98095" result="effect1_foregroundBlur_886_5692" />
          <feTurbulence type="fractalNoise" baseFrequency="39.375003814697266 39.375003814697266" stitchTiles="stitch" numOctaves={3} result="noise" seed={3062} />
          <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
          <feComponentTransfer in="alphaNoise" result="coloredNoise1">
            <feFuncA type="discrete" tableValues="0 0 0 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0" />
          </feComponentTransfer>
          <feComposite operator="in" in2="effect1_foregroundBlur_886_5692" in="coloredNoise1" result="noise1Clipped" />
          <feFlood floodColor="rgba(0, 43, 34, 0.25)" result="color1Flood" />
          <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
          <feMerge result="effect2_noise_886_5692">
            <feMergeNode in="effect1_foregroundBlur_886_5692" />
            <feMergeNode in="color1" />
          </feMerge>
        </filter>
        <filter id="filter4_fn_886_5692" x="49.1238" y="53.781" width="38.9291" height="40.3034" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="4.7873" result="effect1_foregroundBlur_886_5692" />
          <feTurbulence type="fractalNoise" baseFrequency="39.375003814697266 39.375003814697266" stitchTiles="stitch" numOctaves={3} result="noise" seed={3062} />
          <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
          <feComponentTransfer in="alphaNoise" result="coloredNoise1">
            <feFuncA type="discrete" tableValues="0 0 0 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0" />
          </feComponentTransfer>
          <feComposite operator="in" in2="effect1_foregroundBlur_886_5692" in="coloredNoise1" result="noise1Clipped" />
          <feFlood floodColor="rgba(0, 43, 34, 0.25)" result="color1Flood" />
          <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
          <feMerge result="effect2_noise_886_5692">
            <feMergeNode in="effect1_foregroundBlur_886_5692" />
            <feMergeNode in="color1" />
          </feMerge>
        </filter>
        <filter id="filter5_fn_886_5692" x="54.5672" y="0.975906" width="56.6371" height="55.5059" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="4.53333" result="effect1_foregroundBlur_886_5692" />
          <feTurbulence type="fractalNoise" baseFrequency="39.375003814697266 39.375003814697266" stitchTiles="stitch" numOctaves={3} result="noise" seed={3062} />
          <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
          <feComponentTransfer in="alphaNoise" result="coloredNoise1">
            <feFuncA type="discrete" tableValues="0 0 0 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0" />
          </feComponentTransfer>
          <feComposite operator="in" in2="effect1_foregroundBlur_886_5692" in="coloredNoise1" result="noise1Clipped" />
          <feFlood floodColor="rgba(0, 43, 34, 0.25)" result="color1Flood" />
          <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
          <feMerge result="effect2_noise_886_5692">
            <feMergeNode in="effect1_foregroundBlur_886_5692" />
            <feMergeNode in="color1" />
          </feMerge>
        </filter>
        <filter id="filter6_fn_886_5692" x="60.4892" y="57.6337" width="34.1717" height="34.2558" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="3.98095" result="effect1_foregroundBlur_886_5692" />
          <feTurbulence type="fractalNoise" baseFrequency="39.375003814697266 39.375003814697266" stitchTiles="stitch" numOctaves={3} result="noise" seed={3062} />
          <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
          <feComponentTransfer in="alphaNoise" result="coloredNoise1">
            <feFuncA type="discrete" tableValues="0 0 0 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0" />
          </feComponentTransfer>
          <feComposite operator="in" in2="effect1_foregroundBlur_886_5692" in="coloredNoise1" result="noise1Clipped" />
          <feFlood floodColor="rgba(0, 43, 34, 0.25)" result="color1Flood" />
          <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
          <feMerge result="effect2_noise_886_5692">
            <feMergeNode in="effect1_foregroundBlur_886_5692" />
            <feMergeNode in="color1" />
          </feMerge>
        </filter>
        <filter id="filter7_f_886_5692" x="34.0586" y="49.0134" width="31.12" height="38.9339" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="3.98095" result="effect1_foregroundBlur_886_5692" />
        </filter>
        <filter id="filter8_fn_886_5692" x="71.5786" y="16.8656" width="35.7037" height="37.078" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="3.98095" result="effect1_foregroundBlur_886_5692" />
          <feTurbulence type="fractalNoise" baseFrequency="39.375003814697266 39.375003814697266" stitchTiles="stitch" numOctaves={3} result="noise" seed={3062} />
          <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
          <feComponentTransfer in="alphaNoise" result="coloredNoise1">
            <feFuncA type="discrete" tableValues="0 0 0 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0" />
          </feComponentTransfer>
          <feComposite operator="in" in2="effect1_foregroundBlur_886_5692" in="coloredNoise1" result="noise1Clipped" />
          <feFlood floodColor="rgba(0, 43, 34, 0.25)" result="color1Flood" />
          <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
          <feMerge result="effect2_noise_886_5692">
            <feMergeNode in="effect1_foregroundBlur_886_5692" />
            <feMergeNode in="color1" />
          </feMerge>
        </filter>
        <filter id="filter9_fn_886_5692" x="54.8481" y="38.1332" width="26.1595" height="32.1781" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="3.98095" result="effect1_foregroundBlur_886_5692" />
          <feTurbulence type="fractalNoise" baseFrequency="39.375003814697266 39.375003814697266" stitchTiles="stitch" numOctaves={3} result="noise" seed={3062} />
          <feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise" />
          <feComponentTransfer in="alphaNoise" result="coloredNoise1">
            <feFuncA type="discrete" tableValues="0 0 0 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0" />
          </feComponentTransfer>
          <feComposite operator="in" in2="effect1_foregroundBlur_886_5692" in="coloredNoise1" result="noise1Clipped" />
          <feFlood floodColor="rgba(0, 43, 34, 0.25)" result="color1Flood" />
          <feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1" />
          <feMerge result="effect2_noise_886_5692">
            <feMergeNode in="effect1_foregroundBlur_886_5692" />
            <feMergeNode in="color1" />
          </feMerge>
        </filter>
        <linearGradient id="paint0_linear_886_5692" x1="72.8664" y1="60" x2="72.8664" y2="-11.5" gradientUnits="userSpaceOnUse">
          <stop stopColor="#154544" stopOpacity="0" style={{ stopColor: 'var(--illustration-cloud-from)' }} />
          <stop offset="1" stopColor="#0D3538" style={{ stopColor: 'var(--illustration-cloud-to)' }} />
        </linearGradient>
      </defs>
    </svg>
  )
}

export function FileUploadZone({
  variant = 'dataset',
  accept,
  multiple = false,
  disabled = false,
  onFilesSelected,
  className,
  title,
  subtitle,
  hideIllustration = false,
  icon,
}: FileUploadZoneProps) {
  const { t } = useTranslation('app')
  const [isDragOver, setIsDragOver] = useState(false)
  const zoneRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      if (!disabled) setIsDragOver(true)
    },
    [disabled],
  )

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (zoneRef.current && !zoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragOver(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragOver(false)
      if (disabled) return
      if (e.dataTransfer.files.length > 0) {
        onFilesSelected(e.dataTransfer.files)
      }
    },
    [disabled, onFilesSelected],
  )

  const handleClick = useCallback(() => {
    if (!disabled) inputRef.current?.click()
  }, [disabled])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        onFilesSelected(e.target.files)
      }
      if (inputRef.current) inputRef.current.value = ''
    },
    [onFilesSelected],
  )

  if (variant === 'context') {
    return (
      <div
        ref={zoneRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleClick()
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center gap-1 cursor-pointer px-6 py-10 transition-colors',
          isDragOver && 'opacity-70',
          disabled && 'pointer-events-none opacity-50',
          className,
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          className="hidden"
          disabled={disabled}
        />
        {icon ? icon : !hideIllustration && <ContextUploadIllustration />}
        <div className="flex flex-col items-center text-center">
          {title ? (
            <p className="text-base font-medium leading-[1.6] text-foreground dark:text-white">{title}</p>
          ) : (
            <p className="text-base font-medium leading-[1.6] text-foreground dark:text-white">
              <span>{t('audienceCreate.dropContext')}</span>
              <span className="text-primary underline">{t('audienceCreate.clickBrowse')}</span>
            </p>
          )}
          <p className="text-base leading-[1.6] text-muted-foreground">
            {subtitle ?? t('audienceCreate.contextFormats')}
          </p>
        </div>
      </div>
    )
  }

  // dataset variant — left-aligned inline layout
  return (
    <div
      ref={zoneRef}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleClick()
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        'flex items-center gap-3 cursor-pointer transition-colors',
        isDragOver && 'opacity-70',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        className="hidden"
        disabled={disabled}
      />
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <UploadCloud className="h-4 w-4 text-foreground" />
      </div>
      <div className="flex flex-col">
        {title ? (
          <p className="text-base leading-[1.6] text-foreground dark:text-white">{title}</p>
        ) : (
          <p className="text-base leading-[1.6] text-foreground dark:text-white">
            <span>{t('audienceCreate.dropDataset')}</span>
            <span className="font-medium text-primary underline">{t('audienceCreate.clickBrowse')}</span>
          </p>
        )}
        <p className="text-sm leading-[1.5] text-muted-foreground">{subtitle ?? t('audienceCreate.datasetFormats')}</p>
      </div>
    </div>
  )
}
